import { FC } from 'react';
import { subSeconds } from 'date-fns';

import { Scroller } from '@rikstv/play-common/src/components/scroller/Scroller';
import config from '@rikstv/play-common/src/config';
import { Channel, Program } from '@rikstv/play-common/src/forces/channels/channel.types';
import { captureExceptionInSentry } from '@rikstv/play-common/src/utils/errorTracker/tracking';
import { Tag, TertiaryButton } from '@rikstv/shared-components';

import { dateNow } from '../ProgressBar/progressBar.utils';

import styles from './ProgramPoints.module.css';

interface Props {
  channel: Channel;
}

export const programFilter = (channel: Channel, program: Program) => {
  const now = dateNow();
  const broadcastStartTime = dateNow(program.time);
  const bufferLengthSeconds = channel.bufferLength ? channel.bufferLength / 1000 : config.player.liveBufferInSeconds;
  const bufferStart = subSeconds(now, bufferLengthSeconds);
  return bufferStart < broadcastStartTime && broadcastStartTime < now;
};

export const ProgramPoints: FC<Props> = ({ channel }) => {
  const programs = channel.programs.filter(p => programFilter(channel, p));

  const setTheoCurrentTime = (startAtSeconds: number) => {
    const theoPlayer = window.THEOplayer;
    if (!theoPlayer?.players?.length) {
      captureExceptionInSentry(new Error('THEOplayer instance not found when trying to seek to program point.'));
      return;
    }
    const player = theoPlayer.players[0];
    const streamEndSeconds = player.seekable.end(0);
    const nowEpoch = new Date().getTime() / 1000;
    const secondsRewind = nowEpoch - startAtSeconds;
    player.currentTime = streamEndSeconds - secondsRewind;
  };

  return (
    <Scroller>
      {programs.map((program, idx) => {
        const now = dateNow();
        const broadcastStartTime = dateNow(program.time);
        const broadcastEndTime = new Date(broadcastStartTime.getTime() + program.duration * 1000);
        const isLive = broadcastStartTime <= now && now <= broadcastEndTime;
        const prefix = isLive ? (
          <Tag className={styles.liveTag}>Nå </Tag>
        ) : (
          `${new Date(program.time).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h24',
          })} `
        );

        return (
          <TertiaryButton
            className={styles.programPoint}
            key={`${program.name}-${program.time}-${idx}`}
            onClick={() => setTheoCurrentTime(new Date(program.time).getTime() / 1000)}>
            {prefix}
            {program.seriesName}
          </TertiaryButton>
        );
      })}
    </Scroller>
  );
};
